import { Badge, Skeleton, TBody, TD, TH, THead, TR, Table, TableWrap } from '@/components/ui';
import { EmailLink, TextLink } from '@/components/ui/TextLink';
import { formatDateTime } from '@/lib/format';
import type { EmailThread } from '@/lib/types';
import { MATCH_STATUS_LABELS, MATCH_STATUS_TONES, addressLabel } from './email-log-format';

/*
 * The Email Log list: a desktop table and, below `md`, a stacked card list with
 * the same columns. A row opens the page's single EmailThreadModal.
 */

const TABLE_STYLE = { minWidth: '900px' };
const SKELETON_ROWS = 8;
const MAX_PARTICIPANTS = 3;

function Participants({ thread }: { thread: EmailThread }) {
  const shown = thread.participants.slice(0, MAX_PARTICIPANTS);
  const more = thread.participants.length - shown.length;
  if (shown.length === 0) return <span className="text-fg-subtle">—</span>;
  return (
    <span className="flex min-w-0 flex-wrap gap-x-2 gap-y-0.5">
      {shown.map((addr, index) => (
        <span key={`${addr.address}-${index}`} className="min-w-0">
          <EmailLink
            address={addr.address}
            title={addr.address}
            onClick={(event) => event.stopPropagation()}
          >
            {addressLabel(addr)}
          </EmailLink>
          {index < shown.length - 1 || more > 0 ? ',' : ''}
        </span>
      ))}
      {more > 0 && <span className="text-fg-muted">+{more}</span>}
    </span>
  );
}

function CompanyCell({ thread }: { thread: EmailThread }) {
  if (thread.company) {
    return (
      <TextLink to={`/companies/${thread.company.id}`} onClick={(event) => event.stopPropagation()}>
        {thread.company.name}
      </TextLink>
    );
  }
  if (thread.candidateCompanies.length > 0) {
    return (
      <span className="flex min-w-0 flex-wrap gap-x-2 gap-y-0.5 text-fg-secondary">
        {thread.candidateCompanies.map((candidate, index) => (
          <span key={candidate.id}>
            <TextLink
              to={`/companies/${candidate.id}`}
              tone="muted"
              onClick={(event) => event.stopPropagation()}
            >
              {candidate.name}
            </TextLink>
            {index < thread.candidateCompanies.length - 1 ? ',' : ''}
          </span>
        ))}
      </span>
    );
  }
  return <span className="text-fg-subtle">—</span>;
}

function SubjectButton({ thread, onOpen }: { thread: EmailThread; onOpen: (thread: EmailThread) => void }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onOpen(thread);
      }}
      className="min-w-0 rounded-sm text-left font-medium text-fg underline-offset-2 [overflow-wrap:anywhere] hover:underline focus-visible:underline"
    >
      {thread.subject || '(no subject)'}
    </button>
  );
}

function LastMessage({ thread }: { thread: EmailThread }) {
  return thread.lastMessageAt ? (
    <time dateTime={thread.lastMessageAt} className="tabular">
      {formatDateTime(thread.lastMessageAt)}
    </time>
  ) : (
    <span className="text-fg-subtle">—</span>
  );
}

export function EmailThreadList({
  rows,
  loading,
  onOpen,
}: {
  rows: readonly EmailThread[];
  loading: boolean;
  onOpen: (thread: EmailThread) => void;
}) {
  if (loading && rows.length === 0) {
    return (
      <>
        <div className="hidden md:block">
          <TableWrap frozenHeader>
            <Table style={TABLE_STYLE}>
              <ThreadTableHead />
              <TBody>
                {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                  <TR key={i}>
                    {Array.from({ length: 5 }, (_, j) => (
                      <TD key={j}>
                        <Skeleton className="h-3 w-24" />
                      </TD>
                    ))}
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        </div>
        <div className="grid gap-2 p-4 md:hidden">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="hidden md:block">
        <TableWrap frozenHeader>
          <Table style={TABLE_STYLE}>
            <ThreadTableHead />
            <TBody>
              {rows.map((thread) => (
                <TR key={thread.id} interactive className="cursor-pointer" onClick={() => onOpen(thread)}>
                  <TD>
                    <div className="grid min-w-0 justify-items-start gap-1">
                      <SubjectButton thread={thread} onOpen={onOpen} />
                      <Badge tone={MATCH_STATUS_TONES[thread.matchStatus]} dot>
                        {MATCH_STATUS_LABELS[thread.matchStatus]}
                      </Badge>
                    </div>
                  </TD>
                  <TD>
                    <Participants thread={thread} />
                  </TD>
                  <TD className="whitespace-nowrap text-fg-secondary">
                    <LastMessage thread={thread} />
                  </TD>
                  <TD className="tabular text-right">{thread.messageCount}</TD>
                  <TD>
                    <CompanyCell thread={thread} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      </div>

      <ul className="m-0 grid list-none gap-2 p-4 md:hidden">
        {rows.map((thread) => (
          <li
            key={thread.id}
            className="grid cursor-pointer gap-2 rounded-lg border border-border bg-surface p-3"
            onClick={() => onOpen(thread)}
          >
            <div className="flex items-start justify-between gap-2">
              <SubjectButton thread={thread} onOpen={onOpen} />
              <Badge tone={MATCH_STATUS_TONES[thread.matchStatus]} dot>
                {MATCH_STATUS_LABELS[thread.matchStatus]}
              </Badge>
            </div>
            <dl className="m-0 grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-2 gap-y-1 text-xs">
              <dt className="text-fg-muted">Participants</dt>
              <dd className="m-0 min-w-0">
                <Participants thread={thread} />
              </dd>
              <dt className="text-fg-muted">Last message</dt>
              <dd className="m-0">
                <LastMessage thread={thread} />
              </dd>
              <dt className="text-fg-muted">Messages</dt>
              <dd className="m-0 tabular">{thread.messageCount}</dd>
              <dt className="text-fg-muted">Company</dt>
              <dd className="m-0 min-w-0">
                <CompanyCell thread={thread} />
              </dd>
            </dl>
          </li>
        ))}
      </ul>
    </>
  );
}

function ThreadTableHead() {
  return (
    <THead>
      <TR>
        <TH>Subject</TH>
        <TH className="w-64">Participants</TH>
        <TH className="w-44">Last message</TH>
        <TH className="w-24 text-right">Messages</TH>
        <TH className="w-56">Company</TH>
      </TR>
    </THead>
  );
}
